import type { UseMutationProps } from '@ztechapps/common.hook'
import { useMutation } from '@ztechapps/common.hook'
import { gql } from '@apollo/client'
import type { GenerateProductNameByAiArgs } from '@ztechapps/common.contract'

export const generateProductNameByAiQuery = gql`
  mutation generateProductNameByAi($input: GenerateProductNameByAiArgs!) {
    generateProductNameByAi(input: $input)
  }
`

const defaultQuery = generateProductNameByAiQuery

/**
 * Hook to generate product name using AI
 *
 * @description
 * This hook provides a mutation function to generate AI-powered product names.
 * It uses the GraphQL `generateProductNameByAi` mutation which accepts a productId
 * and returns a generated name string.
 *
 * @example
 * ```tsx
 * const [generateName, , { loading, error }] = useGenerateProductNameByAi()
 *
 * const handleGenerate = async () => {
 *   const result = await generateName({
 *     input: { productId: 'product-123' }
 *   })
 *   const generatedName = result?.data?.generateProductNameByAi
 * }
 * ```
 *
 * @param props - Mutation configuration props
 * @returns Tuple containing [mutateFunction, apolloMutation, mutationResult]
 */
export const useGenerateProductNameByAi = ({ query, ...props } = {} as UseMutationProps) => {
  query = query ?? defaultQuery

  return useMutation<{ generateProductNameByAi: string }, GenerateProductNameByAiArgs>({ query, ...props })
}
